import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Input,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useAuth from 'auth/UseAuth';
import { ChangeEvent, useState } from 'react';
import { appSettings } from 'AppSettings';
import { InvenvtoryCheckRequestUploadResults } from 'data/InventoryCheckRequest';
import { DialogCloseButton } from '../../components/DialogCloseButton';
import { FileUploadStatus } from 'helpers/FileProcessing';
import { PersistentDialog } from '../../components/PersistentDialog';

export interface UpdateInventoryCheckRequestModalProps {
  onUpdateInventoryCheckRequest: Function;
}

export const UpdateInventoryCheckRequestModal = ({
  onUpdateInventoryCheckRequest,
}: UpdateInventoryCheckRequestModalProps) => {
  const { accessToken } = useAuth();
  const theme = useTheme();

  const [open, setOpen] = useState<boolean>(false);
  const [fileUploadStatus, setFileUploadStatus] = useState<FileUploadStatus>('NotStarted');
  const [uploadResults, setUploadResults] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleModalButtonClick = () => {
    setOpen(true);
  };

  const handleUpdaterSaveButtonClick = async () => {
    setFileUploadStatus('Uploading');

    if (accessToken && selectedFile) {
      try {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('filename', selectedFile.name);

        const response = await fetch(`${appSettings.api.endpoint}/api/v2/InventoryCheckRequests`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });

        if (response.ok) {
          let results = (await response.json()) as InvenvtoryCheckRequestUploadResults;
          const samplesAvailable = `\n-  ${results.numYesAvailable} ${sample(results.numYesAvailable)} available`;
          const samplesNotAvailable = `\n-  ${results.numNoAvailable} ${sample(results.numNoAvailable)} not available`;
          const samplesNotChecked =
            results.numNullAvailable === 0
              ? ''
              : `\n-  ${results.numNullAvailable} ${sample(results.numNullAvailable)} not checked`;
          const invlaidSamples =
            results.numInvalidSampleIds === 0
              ? ''
              : `\n-  ${results.numInvalidSampleIds} invalid sample ${id(results.numInvalidSampleIds)}`;

          setUploadResults(
            `Upload results:${samplesAvailable}${samplesNotAvailable}${samplesNotChecked}${invlaidSamples}`
          );
          setFileUploadStatus('Complete');
          onUpdateInventoryCheckRequest();
        } else {
          let responseBody: { message: string } = await response.json();
          setFileUploadStatus('Error');

          if (responseBody.message) {
            setErrorMessage(responseBody.message);
          } else if (response.status === 401) {
            setErrorMessage('Error: unauthorized user.');
          } else {
            setErrorMessage('Unknown error.');
          }
        }
      } catch (err) {
        setFileUploadStatus('Error');
        setErrorMessage('Unknown error.');
      }
    } else {
      setFileUploadStatus('NotStarted');
    }
  };

  const handleUpdaterClose = () => {
    clearSelectedState();
    setOpen(false);
  };

  const clearSelectedState = () => {
    setFileUploadStatus('NotStarted');
    setErrorMessage(undefined);
    setSelectedFile(null);
  };

  const handleFileSelection = async (e: ChangeEvent<HTMLInputElement>) => {
    setFileUploadStatus('NotStarted');
    if (e.target.files) {
      setSelectedFile(e.target.files[0]);
    }
  };

  return (
    <>
      <Button
        onClick={handleModalButtonClick}
        sx={[
          {
            color: theme.colors.alpha.black[100],
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        ]}
      >
        <FileUploadIcon />
        <Typography variant='button' ml={1}>
          Upload Completed Request
        </Typography>
      </Button>
      <PersistentDialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          <DialogCloseButton onClick={handleUpdaterClose} />
          <Typography variant='h3' gutterBottom>
            Update Inventory Check Request
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box component='form' mt={2}>
            <Typography variant='h5' mt={2}>
              Select completed inventory check request
            </Typography>
            <Input type='file' onChange={handleFileSelection} sx={{ mt: 2, minWidth: 500 }} />
          </Box>
          {fileUploadStatus === 'Complete' && (
            <DialogContentText display='flex' alignItems='center' mt={2} whiteSpace='pre'>
              {uploadResults}
            </DialogContentText>
          )}
          {fileUploadStatus === 'Error' && (
            <DialogContentText display='flex' alignItems='center' mt={2} color='error'>
              <ErrorOutlineIcon sx={{ mr: 1 }} />
              {errorMessage}
            </DialogContentText>
          )}
        </DialogContent>
        {fileUploadStatus === 'Uploading' && <LinearProgress />}
        <DialogActions>
          <Button onClick={handleUpdaterSaveButtonClick} disabled={fileUploadStatus === 'Uploading'}>
            Save
          </Button>
          <Button onClick={handleUpdaterClose} disabled={fileUploadStatus === 'Uploading'}>
            Close
          </Button>
        </DialogActions>
      </PersistentDialog>
    </>
  );
};

function sample(count: number) {
  return count === 1 ? 'sample' : 'samples';
}

function id(count: number) {
  return count === 1 ? 'ID' : 'Ids';
}
